/**
 * Aura AGM — 자체완결 입력 엔진 (provide/inject).
 *
 * 인코퍼레이션 모듈과 분리된 **독립 cdd**. 패널이 provideAgmFill() 로 제공하고,
 * CddVarField / DocScope 가 inject 한다. 값은 평면 문자열 맵(values), 문서 스코프는 'scope/key'.
 */
import { reactive, ref, computed, provide, inject, type InjectionKey, type ComputedRef, type Ref } from 'vue';

export type AgmFieldType = 'text' | 'date' | 'address' | 'entity' | 'person' | 'select' | 'yesno';

/** 회사/사람 공유 키 — 스코프가 달라도 같은 값을 공유(정규화용). */
const SHARED_ENTITY_KEYS = new Set([
  'company_name',
  'company_registration_no',
  'company_uen', // 싱가푸라 UEN — 스코프 문서(ROND/RONS/RORC)에서도 최상위 값 공유
  'registered_office_address',
  // 회사 비서(Notice 발송자) — 전 Notice 부수에 같은 값 공유(스코프 무관).
  'secretary_name',
  // 재무연도 — DDF(ddf_ 스코프) 이해관계 표 헤더에서도 회사 최상위값을 공유(부수별 재입력 방지).
  'financial_year_end',
  'financial_year_start_date',
]);

/** scope/key 에서 bare key 만. */
export function bareKey(k: string): string {
  const i = k.lastIndexOf('/');
  return i >= 0 ? k.slice(i + 1) : k;
}
export function scopeOf(k: string): string {
  const i = k.lastIndexOf('/');
  return i >= 0 ? k.slice(0, i + 1) : '';
}

/** 필드 종류 판정 — 라벨/위젯 분기용. */
export function typeOf(key: string): AgmFieldType {
  const b = bareKey(key);
  if (b === 'company_name') return 'entity';
  // 지주회사명·감사인명은 _name 로 끝나지만 사람이 아니라 텍스트(person 조회 X).
  if (b === 'parent_company_name' || b === 'auditors_name' || b === 'controller_entity_name'
    || b === 'controller_corporate_register_name' || b === 'controller_legal_form'
    || b === 'controller_jurisdiction') return 'text';
  if (/(^|_)date$|_date$|dated_on$|date_of_|_becoming$|_cessation$|_ceased|financial_year_end$|financial_year_start_date$|meeting_time$/.test(b)) {
    return b === 'meeting_time' ? 'text' : 'date';
  }
  if (/^(is_|has_|declare_)/.test(b)) return 'yesno';
  if (b === 'company_type' || b === 'company_activity' || b === 'company_solvency'
    || /_location$/.test(b)) return 'select';
  if (/(registered_office_address|_address)$/.test(b) || b === 'meeting_venue') return 'address';
  if (/_name$|_holder$/.test(b) && b !== 'company_name') return 'person';
  return 'text';
}

/** 사람 이름 필드에 붙는 짝 주소/식별 키(자동 채움용). */
export interface AgmFillCtx {
  values: Record<string, string>;
  keys: ComputedRef<string[]>;
  totalFields: ComputedRef<number>;
  filledCount: ComputedRef<number>;
  activeKey: Ref<string>;
  typeOf: (key: string) => AgmFieldType;
  setValue: (key: string, val: string) => void;
  getValue: (key: string) => string;
  exportPayload: () => Record<string, string>;
  registerField: (key: string) => void;
  unregisterField: (key: string) => void;
  setActivateHandler: (fn: ((key: string) => void) | null) => void;
  setAddressHandler: (fn: ((key: string) => void) | null) => void;
  markActive: (key: string) => void;
  activate: (key: string) => void;
  requestAddress: (key: string) => void;
  applyPersonToActive: (person: Record<string, any>) => void;
  applyPersonToKey: (key: string, person: Record<string, any>) => void;
  setAfterChangeHandler: (fn: (() => void) | null) => void;
}

export const AGM_FILL: InjectionKey<AgmFillCtx> = Symbol('auraAgmFill');
export const DOC_SCOPE: InjectionKey<string> = Symbol('auraAgmDocScope');

function isFilled(v: unknown): boolean {
  return !!String(v ?? '').trim();
}

export function provideAgmFill(): AgmFillCtx {
  const values = reactive<Record<string, string>>({});
  const registered = reactive<Record<string, number>>({});
  const activeKey = ref('');
  let activateHandler: ((key: string) => void) | null = null;
  let addressHandler: ((key: string) => void) | null = null;
  let afterChange: (() => void) | null = null;

  const keys = computed(() => Object.keys(registered).filter((k) => registered[k] > 0));
  const totalFields = computed(() => keys.value.length);
  const filledCount = computed(() => keys.value.filter((k) => isFilled(values[k])).length);

  function propagateShared(key: string, val: string) {
    const bare = bareKey(key);
    if (!SHARED_ENTITY_KEYS.has(bare)) return;
    // 같은 bare 키(스코프 무관)에 전파 — 회사명/등록번호/주소는 문서 간 공유.
    for (const k of Object.keys(values)) {
      if (k !== key && bareKey(k) === bare && values[k] !== val) values[k] = val;
    }
  }

  function setValue(key: string, val: string) {
    const v = String(val ?? '');
    if (values[key] === v) return;
    values[key] = v;
    propagateShared(key, v);
    if (afterChange) afterChange();
  }
  function getValue(key: string): string {
    // 스코프 값 우선, 없으면 최상위(bare) 공유값.
    if (isFilled(values[key])) return values[key];
    const bare = bareKey(key);
    if (SHARED_ENTITY_KEYS.has(bare) && key !== bare && isFilled(values[bare])) return values[bare];
    return values[key] ?? '';
  }

  function registerField(key: string) {
    registered[key] = (registered[key] || 0) + 1;
    if (!(key in values)) values[key] = '';
  }
  function unregisterField(key: string) {
    if (registered[key]) registered[key] -= 1;
  }

  function exportPayload(): Record<string, string> {
    return { ...values };
  }

  function applyPersonToKey(key: string, person: Record<string, any>) {
    const name = String(
      person?.name || person?.contactName || person?.fullName
        || `${person?.firstName || ''} ${person?.lastName || ''}`.trim() || '',
    ).trim();
    if (name) setValue(key, name);
    const sc = scopeOf(key);
    const stem = bareKey(key).replace(/_name$|_holder$/, '');
    const addr = String(person?.residentialAddress || person?.address || person?.primaryAddress || '').trim();
    const id = String(person?.identificationNo || person?.nricNo || person?.idNo || person?.contactRegNo || '').trim();
    const nat = String(person?.nationalityName || person?.nationality || '').trim();
    const email = String(person?.email || '').trim();
    const dob = String(person?.dateOfBirth || person?.dob || '').trim();
    if (addr) { setValue(sc + `${stem}_residential_address`, addr); setValue(sc + `${stem}_address`, addr); }
    if (id) setValue(sc + `${stem}_identification_no`, id);
    if (nat) setValue(sc + `${stem}_nationality`, nat);
    if (email) setValue(sc + `${stem}_email`, email);
    if (dob) setValue(sc + `${stem}_date_of_birth`, dob);
  }

  const ctx: AgmFillCtx = {
    values,
    keys,
    totalFields,
    filledCount,
    activeKey,
    typeOf,
    setValue,
    getValue,
    exportPayload,
    registerField,
    unregisterField,
    setActivateHandler: (fn) => { activateHandler = fn; },
    setAddressHandler: (fn) => { addressHandler = fn; },
    markActive: (key) => { activeKey.value = key; },
    activate: (key) => { activeKey.value = key; if (activateHandler) activateHandler(key); },
    requestAddress: (key) => { activeKey.value = key; if (addressHandler) addressHandler(key); },
    applyPersonToActive: (person) => { if (activeKey.value) applyPersonToKey(activeKey.value, person); },
    applyPersonToKey,
    setAfterChangeHandler: (fn) => { afterChange = fn; },
  };
  provide(AGM_FILL, ctx);
  return ctx;
}

export function useAgmFill(): AgmFillCtx {
  const ctx = inject(AGM_FILL, null);
  if (!ctx) throw new Error('useAgmFill() must be used within a component that called provideAgmFill()');
  return ctx;
}
